import * as React from 'react';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';
import { Modal, Table, Button, Tag } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import { useAppStore } from '../../stores';
import { WorksheetModel } from '../../models';
import { WorksheetChangeDto } from '../../types';
import { TypeUtils } from '../../../common/misc';
import SharedUtils from '../../../custom_shared/misc/Utils';
import './WorksheetChangesModal.less';

interface Props {
    worksheet: WorksheetModel;
}

const toDateTime = (value: unknown): DateTime | null => {
    const date = SharedUtils.convertDateValueToDate(value);

    if (!date) {
        return null;
    }

    const dateTime = DateTime.fromJSDate(date);
    return dateTime.isValid ? dateTime : null;
};

const WorksheetChangesModal: React.FC<Props> = ({ worksheet }) => {
    const { worksheetsChangesStore, sessionStore } = useAppStore();

    const onClose = () => worksheetsChangesStore.setIsChangesModalVisible(false);

    const getChangeType = (change: WorksheetChangeDto) => {
        switch (change.type) {
            case 'InputModified':
                return 'Input Updated';
            case 'EntryCodeAdded':
                return 'Entry Code Added';
            case 'EntryCodeRemoved':
                return 'Entry Code Removed';
            case 'BalanceInformationAdded':
                return 'Balance Information Added';
            default:
                return 'Unknown Change';
        }
    };

    const getFieldValue = (change: WorksheetChangeDto, label: string) => {
        const field = change.fields.find(f => f.name === label);
        return field?.value;
    };

    const formatValue = (value: unknown, dataType: unknown): string => {
        if (!value) {
            return '';
        }

        try {
            switch (dataType) {
                case 'DateTime':
                    return toDateTime(value)?.toFormat('yyyy-MM-dd') ?? 'Invalid Date';
                case 'Unknown':
                    return JSON.stringify(value);
                default:
                    return String(value);
            }
        } catch (error) {
            // Fallback to safe string conversion for any formatting errors
            try {
                return JSON.stringify(value);
            } catch {
                return 'Invalid Value';
            }
        }
    };

    const columns: ColumnProps<WorksheetChangeDto>[] = [
        {
            title: 'What Changed',
            key: 'label',
            render: (_, record) => {
                const label = getFieldValue(record, 'label');
                return TypeUtils.isString(label) ? label : '';
            }
        },
        {
            title: 'Change Type',
            key: 'type',
            render: (_, record) => getChangeType(record)
        },
        {
            title: 'Triggered By',
            key: 'triggeredBy',
            render: (_, record) => {
                if (record.triggeredBy) {
                    return <Tag color="blue">{record.triggeredBy}</Tag>;
                }
                return <Tag color="green">User</Tag>;
            }
        },
        {
            title: 'Old Value',
            key: 'oldValueFormatted',
            render: (_, record) => {
                const oldValueFormatted = getFieldValue(record, 'oldValueFormatted');
                const dataType = getFieldValue(record, 'dataType');
                return formatValue(oldValueFormatted, dataType);
            }
        },
        {
            title: 'New Value',
            key: 'newValueFormatted',
            render: (_, record) => {
                const value = getFieldValue(record, 'newValueFormatted');
                const dataType = getFieldValue(record, 'dataType');
                return formatValue(value, dataType);
            }
        },
        {
            title: 'Change By',
            dataIndex: 'createdBy',
            key: 'createdBy',
            render: (_, record) => sessionStore.getUserById(record.createdBy)
        },
        {
            title: 'Date',
            dataIndex: 'createDate',
            key: 'createDate',
            defaultSortOrder: 'descend',
            render: (_, record) => toDateTime(record.createDate)?.toFormat('dd.MM.yyyy HH:mm:ss') ?? '',
            sorter: (a, b) => {
                const aMillis = toDateTime(a.createDate)?.toMillis() ?? 0;
                const bMillis = toDateTime(b.createDate)?.toMillis() ?? 0;
                return aMillis - bMillis;
            }
        }
    ];

    return (
        <Modal
            className="accounting-statement-worksheet-changes-modal"
            title="Worksheet Changes"
            visible={worksheetsChangesStore.isChangesModalVisible}
            maskClosable={false}
            width={1400}
            destroyOnClose
            forceRender
            centered
            onCancel={onClose}
            footer={[
                <Button key="close-button" className="light alpha-dialog-button" onClick={onClose}>
                    Close
                </Button>
            ]}
        >
            <Table
                className="worksheet-changes-table"
                dataSource={worksheet.changes}
                columns={columns}
                rowKey={change =>
                    `${worksheet.changes.indexOf(change)}-${change.type}-${change.createDate}-${change.createdBy}`
                }
                pagination={{ pageSize: 20, hideOnSinglePage: true, showSizeChanger: false }}
                scroll={{ y: 800 }}
            />
        </Modal>
    );
};

export default observer(WorksheetChangesModal);
